import { URL } from '@core/constants/url.constant';
import { ApiValidate } from '@core/decorators/ApiValidate.decorator';
import { apiService } from '@core/services/Api.service';
import { generateStringWithQueryParams } from '@shared/utils/generateStringWithQueryParams.util';
import {
  sessionsListSchema,
  createSessionSchema,
  chatHistorySchema,
  getSessionSchema,
  createShareSessionSchema
} from '../schemas/flowChat.schema';
import type {
  TGetSessionsRequest,
  TCreateSessionRequest,
  TGetSessionsResponse,
  TCreateSessionResponse,
  TGetChatHistoryRequest,
  TGetChatHistoryResponse,
  TDeleteSessionRequest,
  TUpdateSessionRequest,
  TUpdateSessionResponse,
  TAbortAnswerGenerationRequest,
  TGetChatSessionRequest,
  TGetChatSessionResponse,
  TCreateShareSession
} from '../models/flowChat.model';

class FlowChatService {
  @ApiValidate(sessionsListSchema)
  public getSessions({ flowId, limit, offset, order }: TGetSessionsRequest) {
    return apiService.get<TGetSessionsResponse>({
      url: `${URL.flow}/${flowId}/assistant/sessions${generateStringWithQueryParams({ limit, offset, order })}`
    });
  }

  @ApiValidate(createSessionSchema)
  public createSession({ flowId, dto }: TCreateSessionRequest) {
    return apiService.post<TCreateSessionResponse>({
      url: `${URL.flow}/${flowId}/assistant/sessions`,
      dto
    });
  }

  @ApiValidate(chatHistorySchema)
  public getChatHistory({ flowId, sessionId, limit, offset, order }: TGetChatHistoryRequest) {
    return apiService.get<TGetChatHistoryResponse>({
      url: `${URL.flow}/${flowId}/assistant/sessions/${sessionId}/messages${generateStringWithQueryParams({ limit, offset, order })}`
    });
  }

  @ApiValidate(createSessionSchema)
  public updateSession({ flowId, sessionId, dto }: TUpdateSessionRequest) {
    return apiService.patch<TUpdateSessionResponse>({
      url: `${URL.flow}/${flowId}/assistant/sessions/${sessionId}`,
      dto
    });
  }

  public deleteSession({ flowId, sessionId }: TDeleteSessionRequest) {
    return apiService.delete({
      url: `${URL.flow}/${flowId}/assistant/sessions/${sessionId}`
    });
  }

  public abortAnswerGeneration({ flowId, sessionId }: TAbortAnswerGenerationRequest) {
    return apiService.delete({
      url: `${URL.flow}/${flowId}/assistant/sessions/${sessionId}/abort`
    });
  }

  @ApiValidate(getSessionSchema)
  public getSessionById({ flowId, sessionId }: TGetChatSessionRequest) {
    return apiService.get<TGetChatSessionResponse>({
      url: `${URL.flow}/${flowId}/assistant/sessions/${sessionId}`,
      config: {
        skipErrorNotification: true
      }
    });
  }

  @ApiValidate(createShareSessionSchema)
  public createShareSession(flowId: string, sessionId: string, lastMessageId?: string) {
    return apiService.post<TCreateShareSession>({
      url: `${URL.flow}/${flowId}/assistant/sessions/${sessionId}/share`,
      dto: {
        lastMessageId
      }
    });
  }

  @ApiValidate(createSessionSchema)
  public getShareSession(flowId: string, sessionId: string) {
    return apiService.get<TCreateSessionResponse>({
      url: `${URL.flow}/${flowId}/assistant/sessions/${sessionId}/share`,
      config: {
        skipErrorNotification: true
      }
    });
  }
}

const flowChatService = new FlowChatService();

export { flowChatService };
